import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackPageView } from "./utils/analytics";
import { getPageTitleByPath } from "./utils/pageTitle";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./components/ui/toast/ToastProvider";
import { SidebarProvider } from "./context/SidebarContext";
import { FamilyModeProvider } from "./context/FamilyModeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import { ActiveFamilyProvider } from "./context/ActiveFamilyContext";
import { FamilyPermissionsProvider } from "./context/FamilyPermissionsContext";
import { ChildThemeProvider } from "./context/ChildThemeContext";
import { GlobalTimersWrapper } from "./components/timers/GlobalTimersWrapper";
// import { TutorialProvider } from "./components/onboarding/TutorialProvider"; // OCULTO TEMPORALMENTE - No eliminar
import { FeatureTourProvider } from "./context/FeatureTourContext";
import { FirestoreCacheProvider } from "./context/FirestoreCacheContext";
import { FirebaseCacheProvider } from "./context/FirebaseCacheContext";
import { ScrollToTop } from "./components/common/ScrollToTop";
import LoadingSpinner from "./components/common/LoadingSpinner";
import SkipToContent from "./components/ui/SkipToContent";
import { usePWAUpdate } from "./hooks/usePWAUpdate";
import NotificationPermissionModal from "./components/notifications/NotificationPermissionModal";
import ServiceWorkerErrorHandler from "./components/common/ServiceWorkerErrorHandler";
import ReloadRequiredPrompt from "./components/common/ReloadRequiredPrompt";
import DowngradeWizardWrapper from "./components/downgrade/DowngradeWizardWrapper";
import MemberExpiredPlanModal from "./components/subscription/MemberExpiredPlanModal";
import { Capacitor } from '@capacitor/core';
import { useFirstLoginNotification } from "./hooks/useFirstLoginNotification";
import { useOwnerPlanStatus } from "./hooks/useOwnerPlanStatus";
import { useCapacitorConfig } from "./hooks/useCapacitorConfig";
import { useNativePermissions } from "./hooks/useNativePermissions";
import { usePushNotificationDeeplinks } from "./hooks/usePushNotificationDeeplinks";
import { useAutoRestorePlan } from "./hooks/useAutoRestorePlan";
import { useScreenOrientation } from "./hooks/useScreenOrientation";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase/firebaseConfig';
import { FamilyRealtimeDataProvider } from './context/FamilyRealtimeDataContext';
import { useActiveFamily } from './context/ActiveFamilyContext';
import { getFamilyPlanLimits } from './services/subscriptions';
import { useFamilyMode } from './context/FamilyModeContext';
import { checkUserHasFamily, findUserFamilyFallback } from './utils/authCheck';

// ===== IMPORTACIONES ESTÁTICAS (críticas para carga inicial) =====
import SignIn from "./pages/AuthPages/SignIn";
import SignUp from "./pages/AuthPages/SignUp";
import ResetPassword from "./pages/AuthPages/ResetPassword";
import NotFound from "./pages/OtherPage/NotFound";
import AppLayout from "./layout/AppLayout";
import RequireAuth from "./components/auth/RequireAuth";
import ProtectedFeatureRoute from "./components/features/ProtectedFeatureRoute";

// ===== LAZY LOADING (cargan solo cuando se necesitan) =====
// ⚡ OPTIMIZACIÓN FASE 2: Importaciones optimizadas desde lazyComponents

// Auth Pages
// OnboardingPage eliminado - ahora usamos OnboardingWizard modal

// Legal Pages - Lazy (rara vez se ven)
import { TermsAndConditions, PrivacyPolicy } from './config/lazyComponents';

// UI Elements - Lazy (solo para demo/test)
import {
  Videos,
  Images,
  Alerts,
  Badges,
  Avatars,
  Buttons,
  BasicTables,
} from './config/lazyComponents';

// Charts - Lazy (solo cuando se usan)
import { LineChart, BarChart } from './config/lazyComponents';

// Features opcionales - Lazy
import { Calendar } from './config/lazyComponents';

// Calendario V2 (nuevo) - LEGACY: Excluido del build
// const CalendarV2Page = lazy(() => import('./pages/CalendarV2Page'));

// Asistente IA
const AIAssistant = lazy(() => import('./components/ai/AIAssistant'));

// Groups Pages (funcionalidad principal)
const FamilyDashboard = lazy(() => import("./pages/Groups/Dashboard"));
const HomeFamiliar = lazy(() => import("./pages/Groups/HomeFamiliar"));
const IncomingCallPage = lazy(() => import("./pages/Calls/IncomingCallPage"));
const OutgoingCallPage = lazy(() => import("./pages/Calls/OutgoingCallPage"));
const ActiveCallPage = lazy(() => import("./pages/Calls/ActiveCallPage"));
const BoardFamiliar = lazy(() => import("./pages/Groups/BoardFamiliar"));
const FamilyCalendar = lazy(() => import("./pages/Groups/FamilyCalendar"));
const MemberCalendar = lazy(() => import("./pages/Groups/MemberCalendar"));
const ChecklistList = lazy(() => import("./pages/Groups/ChecklistList"));
const ChecklistForm = lazy(() => import("./pages/Groups/ChecklistForm"));
const ActividadesList = lazy(() => import("./pages/Groups/ActividadesList"));
const ActividadesForm = lazy(() => import("./pages/Groups/ActividadesForm"));
const IntegrantesList = lazy(() => import("./pages/Groups/IntegrantesList"));
const IntegrantesForm = lazy(() => import("./pages/Groups/IntegrantesForm"));
const FamiliasList = lazy(() => import("./pages/Groups/FamiliasList"));
const FamiliasForm = lazy(() => import("./pages/Groups/FamiliasForm"));
const UsuariosList = lazy(() => import("./pages/Groups/UsuariosList"));
const UsuariosForm = lazy(() => import("./pages/Groups/UsuariosForm"));
const EvaluationsList = lazy(() => import("./pages/Groups/EvaluationsList"));
const EvaluationsForm = lazy(() => import("./pages/Groups/EvaluationsForm"));
const SubjectsForm = lazy(() => import("./pages/Groups/SubjectsForm"));
const EvaluationGrades = lazy(() => import("./pages/Groups/EvaluationGrades"));
const GroupsKanban = lazy(() => import("./pages/Groups/GroupsKanban"));
const MyEvaluationsStudent = lazy(() => import("./pages/Groups/MyEvaluationsStudent"));
const SubjectsList = lazy(() => import("./pages/Groups/SubjectsList"));
const AttendancePage = lazy(() => import("./pages/Groups/AttendancePage"));
const QRAttendanceGenerator = lazy(() => import("./pages/Groups/QRAttendanceGenerator"));
const QRAttendanceScanner = lazy(() => import("./pages/Groups/QRAttendanceScanner"));
const ReportsPage = lazy(() => import("./pages/Groups/ReportsPage"));
const PeriodConfiguration = lazy(() => import("./pages/Groups/PeriodConfiguration"));
// Design System (catálogo interno)
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const DesignSystemV2 = lazy(() => import("./pages/DesignSystemV2"));

// Pricing & Onboarding Pages
const PricingPage = lazy(() => import("./pages/PricingPage"));
const OnboardingWelcome = lazy(() => import("./pages/Onboarding/OnboardingWelcome"));
const OnboardingUnitWrapper = lazy(() => import("./pages/Onboarding/OnboardingUnitWrapper"));
const OnboardingMembers = lazy(() => import("./pages/Onboarding/OnboardingMembers"));
const OnboardingSeasonWrapper = lazy(() => import("./pages/Onboarding/OnboardingSeasonWrapper"));
const OnboardingScheduleStep2Wrapper = lazy(() => import("./pages/Onboarding/OnboardingScheduleStep2Wrapper"));
const OnboardingScheduleStep3Wrapper = lazy(() => import("./pages/Onboarding/OnboardingScheduleStep3Wrapper"));
const OnboardingScheduleLocal = lazy(() => import("./pages/Onboarding/OnboardingScheduleLocal"));
const OnboardingTasksWrapper = lazy(() => import("./pages/Onboarding/OnboardingTasksWrapper"));
const OnboardingRewardsWrapper = lazy(() => import("./pages/Onboarding/OnboardingRewardsWrapper"));
const OnboardingScheduleWrapper = lazy(() => import("./pages/Onboarding/OnboardingScheduleWrapper"));
const OnboardingGoals = lazy(() => import("./pages/Onboarding/OnboardingGoals"));
const OnboardingPreview = lazy(() => import("./pages/Onboarding/OnboardingPreview"));
const OnboardingTour = lazy(() => import("./pages/Onboarding/OnboardingTour"));
const OnboardingSelectPlan = lazy(() => import("./pages/Onboarding/OnboardingSelectPlan"));

// Seasons (Desafíos mensuales - LEGACY - OCULTO)
// const SeasonPage = lazy(() => import("./pages/Seasons/SeasonPage"));

// Rutinas (antes: Temporadas)
const RoutinesList = lazy(() => import("./pages/Routines/RoutinesList"));
const RoutineForm = lazy(() => import("./pages/Routines/RoutineForm"));
const RoutineCreationWizard = lazy(() => import("./components/routines/RoutineCreationWizard"));

// Educa - Report Cards
const ReportCardsList = lazy(() => import("./pages/Educa/ReportCardsList"));
const CreateReportCard = lazy(() => import("./pages/Educa/CreateReportCard"));
const CreateUnifiedReportCard = lazy(() => import("./pages/Educa/CreateUnifiedReportCard"));
const UnifiedReportCard = lazy(() => import("./pages/Educa/UnifiedReportCard"));
const ReportCardView = lazy(() => import("./pages/Educa/ReportCardView"));
const ReportCardConfig = lazy(() => import("./pages/Educa/ReportCardConfig"));

// Parents - Vista de padres
const ParentView = lazy(() => import("./pages/Parents/ParentView"));
const ParentDashboard = lazy(() => import("./pages/Parents/ParentDashboard"));
const AcceptInvitation = lazy(() => import("./pages/Parents/AcceptInvitation"));

// Family - Recetas y Planificador
const RecipesList = lazy(() => import("./pages/Family/RecipesList"));
const RecipeForm = lazy(() => import("./pages/Family/RecipeForm"));
const RecipeView = lazy(() => import("./pages/Family/RecipeView"));
const RecipeTemplateLibrary = lazy(() => import("./pages/Family/RecipeTemplateLibrary"));
const MealPlanCalendar = lazy(() => import("./pages/Family/MealPlanCalendar"));
const ShoppingList = lazy(() => import("./pages/Family/ShoppingList"));

// Family - Recompensas
const RewardTemplateLibrary = lazy(() => import("./pages/Family/RewardTemplateLibrary"));


// Communications
const CommunicationsList = lazy(() => import("./pages/Educa/CommunicationsList"));
const CommunicationView = lazy(() => import("./pages/Educa/CommunicationView"));
const CreateCommunication = lazy(() => import("./pages/Educa/CreateCommunication"));

// Templates - OCULTO (biblioteca pausada)
// const TemplateLibrary = lazy(() => import("./pages/Templates/TemplateLibrary"));

// User Pages
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));
const SubscriptionPage = lazy(() => import("./pages/Subscription/SubscriptionPage"));
const ReferralsPage = lazy(() => import("./pages/ReferralsPage"));
const PreferencesPage = lazy(() => import("./pages/Settings/Preferences"));
const Personalization = lazy(() => import("./pages/Settings/Personalization"));
const DataManagement = lazy(() => import("./pages/Settings/DataManagement"));
// SecuritySettings movido a FamiliasForm (configuración del grupo)
// const SecuritySettings = lazy(() => import("./pages/Settings/SecuritySettings"));
const NotificationsPage = lazy(() => import("./pages/Notifications/NotificationsPage"));

// Rewards Marketplace
import {
  Marketplace,
  MyRedemptions,
  RewardsManagement,
  RewardForm,
  RedemptionRequests
} from './config/lazyComponents';

// Rewards Wizard
const RewardConfigurationWizard = lazy(() => import("./components/wizard/RewardConfigurationWizard"));
const MoneyDashboard = lazy(() => import("./components/rewards/MoneyDashboard"));
const BudgetSettings = lazy(() => import("./pages/Rewards/BudgetSettings"));

const HomeRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  const { enabled: familyModeEnabled } = useFamilyMode();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (loading) return;

    const checkUser = async () => {
      if (!user?.uid) {
        setCheckingOnboarding(false);
        return;
      }

      // No interferir si el usuario está en proceso de onboarding o navegando a rutas específicas
      const currentPath = window.location.pathname;
      const isInOnboarding = currentPath.startsWith('/onboarding');
      const isInSpecificRoute = currentPath.startsWith('/groups/') || 
                                currentPath.startsWith('/pricing') ||
                                currentPath.startsWith('/calendar') ||
                                currentPath.startsWith('/settings');
      
      if (isInOnboarding || isInSpecificRoute) {
        setCheckingOnboarding(false);
        setNeedsOnboarding(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        // Si tiene familyId, activeFamilyId, o ya completó el onboarding, no redirigir
        let hasFamily = checkUserHasFamily(userData) || Boolean(localStorage.getItem('activeFamilyId'));

        if (!hasFamily) {
          const fallbackFamId = await findUserFamilyFallback(user.uid, user.email);
          if (fallbackFamId) {
            hasFamily = true;
          }
        }
        
        if (hasFamily) {
          try {
            sessionStorage.removeItem('redirecting_to_onboarding');
            sessionStorage.removeItem('completing_onboarding');
          } catch {}
          setNeedsOnboarding(false);
        } else {
          const isRedirectingToOnboarding = (() => {
            try {
              return sessionStorage.getItem('redirecting_to_onboarding') === 'true';
            } catch {
              return false;
            }
          })();
          setNeedsOnboarding(isRedirectingToOnboarding || !hasFamily);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkUser();
  }, [user?.uid, loading]);

  if (loading || checkingOnboarding) {
    return <LoadingSpinner />;
  }

  if (checkingOnboarding) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (needsOnboarding) {
    let skipped = false;
    try {
      skipped = localStorage.getItem('onboarding_skipped') === '1';
    } catch {
    }
    if (skipped) {
      return <Navigate to="/groups/dashboard" replace />;
    }
    return <Navigate to="/onboarding/welcome" replace />;
  }

  // 🔒 Si está en modo grupo, siempre redirigir a HomeFamiliar
  if (familyModeEnabled) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  return <Navigate to="/groups/dashboard" replace />;
};

// Notification Modal Wrapper - se muestra solo en el primer login
const NotificationModalWrapper: React.FC = () => {
  const { shouldShowModal, markAsAsked } = useFirstLoginNotification();
  
  return (
    <NotificationPermissionModal 
      isOpen={shouldShowModal} 
      onClose={markAsAsked} 
    />
  );
};

const OwnerSubscriptionExpiryCheck: React.FC = () => {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();

  useEffect(() => {
    const run = async () => {
      if (!user?.uid || !activeFamily?.id || !activeFamily?.ownerId) return;
      if (activeFamily.ownerId !== user.uid) return;

      const currentPath = window.location.pathname;
      const publicPaths = [
        '/signin',
        '/sign-in',
        '/login',
        '/signup',
        '/sign-up',
        '/register',
        '/reset-password',
        '/forgot-password',
        '/admin',
        '/onboarding',
        '/verify',
        '/auth',
      ];
      const isPublicPage = publicPaths.some((p) => currentPath.startsWith(p));
      if (isPublicPage) return;

      // Throttle: evitar pegarle a Firestore en cada render/relogin rápido
      const throttleKey = `owner_expiry_check_${activeFamily.id}`;
      const lastMs = Number(localStorage.getItem(throttleKey) || '0');
      const nowMs = Date.now();
      if (nowMs - lastMs < 60_000) return;
      localStorage.setItem(throttleKey, String(nowMs));

      try {
        // Dispara lógica de expiración: si venció, getFamilyPlanLimits crea pending_downgrades (flujo B)
        await getFamilyPlanLimits(activeFamily.id);
      } catch {
        // Silent fail: no bloquear UX por verificación
      }
    };

    run();
  }, [user?.uid, activeFamily?.id, activeFamily?.ownerId]);

  return null;
};

// Member Expired Plan Modal - se muestra a miembros cuando el owner tiene plan vencido
const MemberExpiredPlanWrapper: React.FC = () => {
  const { ownerPlanExpired, loading, ownerName, isOwner } = useOwnerPlanStatus();
  
  // Solo mostrar si NO es owner y el plan del owner está vencido
  const shouldShow = !loading && !isOwner && ownerPlanExpired;
  
  return (
    <MemberExpiredPlanModal 
      isOpen={shouldShow} 
      ownerName={ownerName}
    />
  );
};

// Native Permissions Modal Wrapper - se muestra al instalar la app en móvil
const NativePermissionsWrapper: React.FC = () => {
  const { shouldShowModal, markAsRequested, isNative } = useNativePermissions();
  
  if (!isNative) return null;
  
  return (
    <NotificationPermissionModal 
      isOpen={shouldShowModal} 
      onClose={markAsRequested} 
    />
  );
};

// Onboarding ahora funciona por rutas de páginas separadas: /onboarding/welcome → /onboarding/members → etc.

// Push Notification Deeplinks Wrapper - debe estar dentro del Router
const PushNotificationDeeplinksWrapper: React.FC = () => {
  usePushNotificationDeeplinks();
  return null;
};

// Componente para capturar códigos de referido desde la URL (?ref=... o ?referral=...)
const ReferralTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const refCode = (searchParams.get('ref') || searchParams.get('referral') || '').trim().toUpperCase();
      if (refCode) {
        localStorage.setItem('pending_referral_code', refCode);
      }
    } catch {
      // no-op si falla URLSearchParams
    }
  }, [location.search]);

  return null;
};

// Componente para trackear vistas de páginas en Google Analytics en cada cambio de ruta
const RouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Sincroniza dinámicamente el título de la pestaña del navegador según la ruta activa
const PageTitleManager: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const updateTitle = () => {
      const title = getPageTitleByPath(location.pathname, t);
      if (document.title !== title) {
        document.title = title;
      }
    };

    updateTitle();
    // Micro-delay para prevalecer sobre desmontajes asíncronos de react-helmet
    const timer = setTimeout(updateTitle, 60);
    return () => clearTimeout(timer);
  }, [location.pathname, i18n.language, t]);

  return null;
};

const App: React.FC = () => {
  const isNativePlatform = (() => {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  })();

  // Configurar Capacitor (Status Bar, etc.)
  useCapacitorConfig();
  
  // Auto-restaurar items pausados cuando se renueva el plan
  useAutoRestorePlan();
  
  // Bloquear rotación solo en mobile (< 640px)
  useScreenOrientation();
  
  // Sincronización silenciosa de PWA en segundo plano
  usePWAUpdate();
  
  // Inicializar FCM para push notifications
  useEffect(() => {
    const initFCM = async () => {
      try {
        const fcmService = (await import('./services/fcmService')).default;
        await fcmService.initialize();
      } catch (error) {
        // FCM initialization error silently ignored (non-critical)
      }
    };
    
    initFCM();
  }, []);
  
  return (
    <AuthProvider>
      <FirebaseCacheProvider>
        <FirestoreCacheProvider>
          <ToastProvider>
          <Router>
            <ReferralTracker />
            <RouteTracker />
            <PageTitleManager />
            <SidebarProvider>
              <ActiveFamilyProvider>
                <PreferencesProvider>
                  <FamilyRealtimeDataProvider>
                    <GlobalTimersWrapper>
                      <FamilyPermissionsProvider>
                        <FamilyModeProvider>
                          <SubscriptionProvider>
                          <ChildThemeProvider>
                        {/* <TutorialProvider> OCULTO TEMPORALMENTE - No eliminar */}
                        <FeatureTourProvider>
                        <DowngradeWizardWrapper>
                          <OwnerSubscriptionExpiryCheck />
                          <SkipToContent />
                          <ScrollToTop />
                          <PushNotificationDeeplinksWrapper />
                          <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                    {/* Root smart redirect */}
                    <Route path="/" element={<HomeRedirect />} />
                    {/* Dashboard Layout */}
                    <Route element={<AppLayout />}>

                      {/* Others Page */}
                      <Route
                        path="/calendar"
                        element={
                          <RequireAuth>
                            <Calendar />
                          </RequireAuth>
                        }
                      />
                      <Route path="/groups/calendar" element={<Navigate to="/calendar" replace />} />
                      {/* Calendar V2 (Beta) - Nueva versión modular */}
                      {/* LEGACY: CalendarV2 excluido del build
                      <Route 
                        path="/calendar-v2" 
                        element={
                          <RequireAuth>
                            <CalendarV2Page />
                          </RequireAuth>
                        } 
                      />
                      */}
                      {/* Asistente IA */}
                      <Route 
                        path="/ai-assistant" 
                        element={
                          <RequireAuth>
                            <AIAssistant />
                          </RequireAuth>
                        } 
                      />
                      {/* Forms */}
                      {/* <Route path="/form-elements" element={<FormElements />} /> */}
                      {/* Tables */}
                      <Route path="/basic-tables" element={<BasicTables />} />
                      {/* UI Elements */}
                      <Route path="/alerts" element={<Alerts />} />
                      <Route path="/avatars" element={<Avatars />} />
                      <Route path="/badges" element={<Badges />} />
                      <Route path="/buttons" element={<Buttons />} />
                      <Route path="/images" element={<Images />} />
                      <Route path="/videos" element={<Videos />} />
                      <Route path="/design-system-v2" element={<DesignSystemV2 />} />
                      {/* Charts */}
                      <Route path="/line-chart" element={<LineChart />} />
                      <Route path="/bar-chart" element={<BarChart />} />
                      {/* Productive Family pages (protected) */}
                      {/* Legacy family/member calendar routes kept disabled */}
                      <Route
                        path="/subscription"
                        element={
                          <RequireAuth>
                            <SubscriptionPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/referrals"
                        element={
                          <RequireAuth>
                            <ReferralsPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/preferences"
                        element={
                          <RequireAuth>
                            <PreferencesPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/settings/personalization"
                        element={
                          <RequireAuth>
                            <Personalization />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/settings/data-management"
                        element={
                          <RequireAuth>
                            <DataManagement />
                          </RequireAuth>
                        }
                      />
                      {/* Ruta /security eliminada - PIN ahora se configura en FamiliasForm */}
                      <Route
                        path="/notifications"
                        element={
                          <RequireAuth>
                            <NotificationsPage />
                          </RequireAuth>
                        }
                      />
                      {/* Checklists */}
                      <Route
                        path="/groups/checklist"
                        element={
                          <RequireAuth>
                            <ChecklistList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/checklist/new"
                        element={
                          <RequireAuth requireCanCreateTasks>
                            <ChecklistForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/checklist/:id/edit"
                        element={
                          <RequireAuth requireCanCreateTasks>
                            <ChecklistForm />
                          </RequireAuth>
                        }
                      />
                      {/* Members */}
                      <Route
                        path="/groups/members"
                        element={
                          <RequireAuth requireAdmin>
                            <IntegrantesList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/members/new"
                        element={
                          <RequireAuth requireAdmin>
                            <IntegrantesForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/members/:id/edit"
                        element={
                          <RequireAuth requireAdmin>
                            <IntegrantesForm />
                          </RequireAuth>
                        }
                      />
                      {/* Users routes removed */}
                      {/* Families */}
                      <Route
                        path="/groups/groups"
                        element={
                          <RequireAuth requireAdmin>
                            <FamiliasList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/groups/new"
                        element={
                          <RequireAuth requireAdmin>
                            <FamiliasForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/groups/:id/edit"
                        element={
                          <RequireAuth requireAdmin>
                            <FamiliasForm />
                          </RequireAuth>
                        }
                      />
                      {/* Activities */}
                      <Route
                        path="/groups/activities"
                        element={
                          <RequireAuth>
                            <ActividadesList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/activities/new"
                        element={
                          <RequireAuth requireCanCreateTasks>
                            <ActividadesForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/activities/:id/edit"
                        element={
                          <RequireAuth requireCanCreateTasks>
                            <ActividadesForm />
                          </RequireAuth>
                        }
                      />
                      {/* Rutinas (antes: Temporadas) */}
                      <Route
                        path="/groups/routines"
                        element={
                          <RequireAuth requireAdmin>
                            <RoutinesList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/routines/new"
                        element={
                          <RequireAuth requireAdmin>
                            <RoutineCreationWizard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/routines/:id/edit"
                        element={
                          <RequireAuth requireAdmin>
                            <RoutineCreationWizard />
                          </RequireAuth>
                        }
                      />
                      <Route path="/groups/home-groups" element={<HomeFamiliar />} />
                      <Route path="/calls/incoming/:callId" element={<IncomingCallPage />} />
                      <Route path="/calls/outgoing/:callId" element={<OutgoingCallPage />} />
                      <Route path="/calls/active/:callId" element={<ActiveCallPage />} />
                      {/* Redirect legacy routes */}
                      <Route path="/groups/seasons" element={<Navigate to="/groups/routines" replace />} />
                      <Route path="/groups/seasons/new" element={<Navigate to="/groups/routines/new" replace />} />
                      <Route path="/groups/seasons/:id/edit" element={<Navigate to="/groups/routines/:id/edit" replace />} />
                      {/* Evaluaciones - Solo Logrit Educa */}
                      <Route
                        path="/groups/evaluations"
                        element={
                          <RequireAuth requireAdmin>
                            <EvaluationsList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/evaluations/new"
                        element={
                          <RequireAuth requireAdmin>
                            <EvaluationsForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/evaluations/:id"
                        element={
                          <RequireAuth requireAdmin>
                            <EvaluationsForm />
                          </RequireAuth>
                        }
                      />
                      {/* Mis Calificaciones - Vista Estudiante */}
                      <Route
                        path="/groups/my-grades"
                        element={
                          <RequireAuth>
                            <MyEvaluationsStudent />
                          </RequireAuth>
                        }
                      />
                      {/* Materias - Solo Logrit Educa */}
                      <Route
                        path="/groups/subjects"
                        element={
                          <RequireAuth requireAdmin>
                            <SubjectsList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/subjects/new"
                        element={
                          <RequireAuth requireAdmin>
                            <SubjectsForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/subjects/:id/edit"
                        element={
                          <RequireAuth requireAdmin>
                            <SubjectsForm />
                          </RequireAuth>
                        }
                      />
                      {/* Grilla de Calificaciones */}
                      <Route
                        path="/groups/evaluations/:id/grades"
                        element={
                          <RequireAuth requireAdmin>
                            <EvaluationGrades />
                          </RequireAuth>
                        }
                      />
                      {/* Asignar Grupos (Kanban) */}
                      <Route
                        path="/groups/evaluations/:id/groups"
                        element={
                          <RequireAuth requireAdmin>
                            <GroupsKanban />
                          </RequireAuth>
                        }
                      />
                      {/* Asistencia - Solo Logrit Educa */}
                      <Route
                        path="/groups/attendance"
                        element={
                          <RequireAuth requireAdmin>
                            <AttendancePage />
                          </RequireAuth>
                        }
                      />
                      {/* QR Asistencia - Generador (Maestro) */}
                      <Route
                        path="/groups/attendance/qr/generate"
                        element={
                          <RequireAuth requireAdmin>
                            <QRAttendanceGenerator />
                          </RequireAuth>
                        }
                      />
                      {/* QR Asistencia - Scanner (Estudiante) */}
                      <Route
                        path="/groups/attendance/qr/scan"
                        element={
                          <RequireAuth>
                            <QRAttendanceScanner />
                          </RequireAuth>
                        }
                      />
                      {/* Reportes - Solo Logrit Educa */}
                      <Route
                        path="/groups/reports"
                        element={
                          <RequireAuth requireAdmin>
                            <ReportsPage />
                          </RequireAuth>
                        }
                      />
                      {/* Boletines - Solo Logrit Educa */}
                      <Route
                        path="/educa/report-cards"
                        element={
                          <RequireAuth requireAdmin>
                            <ReportCardsList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/report-cards/create"
                        element={
                          <RequireAuth requireAdmin>
                            <CreateReportCard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/report-cards/:id"
                        element={
                          <RequireAuth>
                            <ReportCardView />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/report-cards/embed/:reportCardId"
                        element={
                          <RequireAuth>
                            <ReportCardView embedded={true} readOnly={true} />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/report-cards/edit/:reportCardId"
                        element={
                          <RequireAuth requireAdmin>
                            <CreateReportCard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/unified-report-card/create"
                        element={
                          <RequireAuth requireAdmin>
                            <CreateUnifiedReportCard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/unified-report-card/:reportCardId"
                        element={
                          <RequireAuth>
                            <UnifiedReportCard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/unified-report-card/embed/:reportCardId"
                        element={
                          <RequireAuth>
                            <UnifiedReportCard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/report-cards/config"
                        element={
                          <RequireAuth requireAdmin>
                            <ReportCardConfig />
                          </RequireAuth>
                        }
                      />
                      {/* Períodos Académicos - Configuración desde FamiliasForm */}
                      <Route
                        path="/groups/periods/config"
                        element={
                          <RequireAuth requireAdmin>
                            <PeriodConfiguration />
                          </RequireAuth>
                        }
                      />
                      {/* Comunicaciones - Solo Logrit Educa */}
                      <Route
                        path="/educa/communications"
                        element={
                          <RequireAuth>
                            <CommunicationsList />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/communications/new"
                        element={
                          <RequireAuth requireAdmin>
                            <CreateCommunication />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/educa/communications/:communicationId"
                        element={
                          <RequireAuth>
                            <CommunicationView />
                          </RequireAuth>
                        }
                      />
                      {/* Templates - OCULTO (biblioteca pausada)
                      <Route
                        path="/templates"
                        element={
                          <RequireAuth>
                            <ProtectedFeatureRoute featureName="templateLibrary">
                              <TemplateLibrary />
                            </ProtectedFeatureRoute>
                          </RequireAuth>
                        }
                      />
                      */}
                      {/* Rewards Marketplace */}
                      <Route
                        path="/rewards"
                        element={
                          <RequireAuth>
                            <Marketplace />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/history"
                        element={
                          <RequireAuth>
                            <MyRedemptions />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/manage"
                        element={
                          <RequireAuth>
                            <RewardsManagement />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/manage/new"
                        element={
                          <RequireAuth>
                            <RewardForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/manage/edit/:id"
                        element={
                          <RequireAuth>
                            <RewardForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/setup-wizard"
                        element={
                          <RequireAuth>
                            <RewardConfigurationWizard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/requests"
                        element={
                          <RequireAuth>
                            <RedemptionRequests />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/money-dashboard"
                        element={
                          <RequireAuth>
                            <MoneyDashboard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/rewards/budget-settings"
                        element={
                          <RequireAuth>
                            <BudgetSettings />
                          </RequireAuth>
                        }
                      />
                      {/* Other family pages */}
                      <Route
                        path="/groups/dashboard"
                        element={
                          <RequireAuth>
                            <FamilyDashboard />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/home-groups"
                        element={
                          <RequireAuth>
                            <HomeFamiliar />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/groups/board"
                        element={
                          <RequireAuth>
                            <BoardFamiliar 
                              memberId={null} 
                              familyId={null} 
                            />
                          </RequireAuth>
                        }
                      />
                      {/* OCULTO - Seasons legacy
                      <Route
                        path="/groups/season"
                        element={
                          <RequireAuth>
                            <SeasonPage />
                          </RequireAuth>
                        }
                      />
                      */}
                      
                      {/* Family - Recetas y Planificador (Solo para licenseType === 'familia') */}
                      <Route
                        path="/family/recipes"
                        element={
                          <RequireAuth>
                            <ProtectedFeatureRoute featureName="advancedRecipes">
                              <RecipesList />
                            </ProtectedFeatureRoute>
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/recipes/biblioteca"
                        element={
                          <RequireAuth>
                            <RecipeTemplateLibrary />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/recipes/create"
                        element={
                          <RequireAuth>
                            <RecipeForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/recipes/:recipeId/edit"
                        element={
                          <RequireAuth>
                            <RecipeForm />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/recipes/:recipeId"
                        element={
                          <RequireAuth>
                            <RecipeView />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/meal-planner"
                        element={
                          <RequireAuth>
                            <ProtectedFeatureRoute featureName="mealPlanCalendar">
                              <MealPlanCalendar />
                            </ProtectedFeatureRoute>
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/family/shopping-list"
                        element={
                          <RequireAuth>
                            <ProtectedFeatureRoute featureName="shoppingList">
                              <ShoppingList />
                            </ProtectedFeatureRoute>
                          </RequireAuth>
                        }
                      />
                      
                      {/* Biblioteca de Recompensas */}
                      <Route
                        path="/family/rewards/biblioteca"
                        element={
                          <RequireAuth>
                            <RewardTemplateLibrary />
                          </RequireAuth>
                        }
                      />
                      
                      <Route
                        path="/profile"
                        element={
                          <RequireAuth>
                            <ProfilePage />
                          </RequireAuth>
                        }
                      />
                    </Route>

                    {/* Auth Layout */}
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    
                    {/* Pricing */}
                    <Route path="/pricing" element={<PricingPage />} />
                    
                    {/* Onboarding Pages (7 pasos) */}
                    <Route path="/onboarding/welcome" element={<OnboardingWelcome />} />
                    <Route path="/onboarding/unit" element={<OnboardingUnitWrapper />} />
                    <Route path="/onboarding/members" element={<OnboardingMembers />} />
                    <Route path="/onboarding/season" element={<OnboardingSeasonWrapper />} />
                    <Route path="/onboarding/schedule-step2" element={<OnboardingScheduleStep2Wrapper />} />
                    <Route path="/onboarding/schedule-step3" element={<OnboardingScheduleStep3Wrapper />} />
                    <Route path="/onboarding/tasks" element={<OnboardingTasksWrapper />} />
                    <Route path="/onboarding/rewards" element={<OnboardingRewardsWrapper />} />
                    <Route path="/onboarding/schedule" element={<OnboardingScheduleLocal />} />
                    <Route path="/onboarding/preview" element={<OnboardingPreview />} />
                    <Route path="/onboarding/tour" element={<OnboardingTour />} />
                    <Route path="/onboarding/plan" element={<OnboardingSelectPlan />} />
                    <Route path="/onboarding/goals" element={<OnboardingGoals />} />
                    <Route path="/onboarding" element={<Navigate to="/onboarding/welcome" replace />} />
                    
                    {/* Legal Pages */}
                    <Route path="/terms" element={<TermsAndConditions />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    

                    {/* Parent Routes - Public/Protected */}
                    {/* Parent Dashboard - Vista multi-estudiante */}
                    <Route
                      path="/parent/dashboard"
                      element={
                        <RequireAuth>
                          <ParentDashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/parent/:memberId"
                      element={
                        <RequireAuth>
                          <ParentView />
                        </RequireAuth>
                      }
                    />
                    {/* Legacy route - redirect */}
                    <Route
                      path="/parent-view/:memberId"
                      element={
                        <RequireAuth>
                          <ParentView />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/parent-invitation/:token"
                      element={<AcceptInvitation />}
                    />

                    {/* Fallback */}
                    <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                          <NotificationModalWrapper />
                          <NativePermissionsWrapper />
                          <MemberExpiredPlanWrapper />
                          <ServiceWorkerErrorHandler />
                          <ReloadRequiredPrompt />
                        </DowngradeWizardWrapper>
          </FeatureTourProvider>
        </ChildThemeProvider>
      </SubscriptionProvider>
    </FamilyModeProvider>
  </FamilyPermissionsProvider>
</GlobalTimersWrapper>
</FamilyRealtimeDataProvider>
</PreferencesProvider>
</ActiveFamilyProvider>
</SidebarProvider>
</Router>
</ToastProvider>
</FirestoreCacheProvider>
</FirebaseCacheProvider>
</AuthProvider>
);
};

export default App;
